import CheckPopupBase from "./index";
// import Dialog from "../../molecules/Dialog";
import { useUser } from "@clerk/clerk-react";
import { differenceInMinutes, format } from "date-fns";
import { BadgeAlert, BadgeCheck, Circle } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { checksHooks2 } from "../../../api";
import { ICheckInputSingleLineViewModel } from "../../../application/viewModels/CheckInputSingleLineViewModel";
import { CheckState } from "../../../infrastructure/api/common/types";
import useZebraLabelPrint from "../../hooks/useZebraLabelPrint";
import { Button } from "../../ui/button";
import CheckStateCard, {
  ActionContainer,
  AnswerBadge,
  AnswerContainer,
  PrimaryActionButton,
  SecondaryActionButton,
} from "./CheckStateCard";

interface ICheckPopupInputSingleLineProps {
  check: ICheckInputSingleLineViewModel;
  onFinish: () => void;
  title: string;
  signed: boolean;
  sectionTitle: string;
  checklistId: number;
  groupUid: string;
}

export const CheckPopupInputSingleLinePrint = observer(
  ({
    check,
    onFinish,
    title,
    signed,
    sectionTitle,
    checklistId,
    groupUid,
  }: ICheckPopupInputSingleLineProps): JSX.Element => {
    // const [dialogOpen, setDialogOpen] = useState(false);
    // const [value, setValue] = useState<string | undefined>(undefined);

    // const handleSubmit = async (value: string) => {
    //   await check.store(value);

    //   setDialogOpen(false);

    //   if (check.finished) {
    //     onFinish();
    //   }
    // };

    const { mutate: mutateReset, isLoading: isLoadingReset } =
      checksHooks2.usePut("/checklists/groups/checks/single-text/value/reset");
    const { mutate: mutateInApplicable, isLoading: isLoadingInApplicable } =
      checksHooks2.usePost(
        "/checklists/groups/checks/single-text/in-applicable",
      );

    const { t } = useTranslation();
    const { user } = useUser();

    const { handlePrint, allowPrint } = useZebraLabelPrint();

    const handleReset = () => {
      // TODO: add translation.
      if (confirm("Weet je zeker dat je het antwoord wilt verwijderen?")) {
        mutateReset(
          {
            checklistId,
            checkUid: check.uid,
            groupUid,
          },
          {
            onSuccess: () => {
              check.reset();
              onFinish();
            },
          },
        );
      }
    };

    function handleCheckIsInApplicable() {
      mutateInApplicable(
        {
          checklistId: checklistId,
          checkUid: check.uid,
          groupUid,
        },
        {
          onSuccess: async () => {
            check.handleInApplicable();
            onFinish();
          },
        },
      );
    }

    return (
      <CheckPopupBase
        activities={check.activities}
        instructionId={check.instructionId}
        signed={signed}
        check={check}
      >
        <CheckStateCard
          signed={signed}
          check={check}
          sectionTitle={sectionTitle}
        >
          {check.state === CheckState.InApplicable && (
            <AnswerContainer>
              <AnswerBadge
                lastTouchedBy={check.lastTouchedBy}
                lastTouchedAt={check.lastTouchedAt}
                checklistAccepted={signed}
                onReset={handleReset}
              >
                <Circle className="w-5 h-5 mr-2" />
                {t("inspectionPage.checkPopup.inApplicableCheckButton")}
              </AnswerBadge>
            </AnswerContainer>
          )}

          {check.text !== null && (
            <AnswerContainer>
              <AnswerBadge
                lastTouchedBy={check.lastTouchedBy}
                lastTouchedAt={check.lastTouchedAt}
                checklistAccepted={signed}
                onReset={handleReset}
                canDelete={false}
              >
                {/* <TextCursorInput className="w-5 h-5 mr-2" /> */}
                <div className="flex flex-col gap-y-1 py-2.5 w-full">
                  <div className="divide-y divide-border/30 divide-dashed">
                    {check.text.split("|").map((text, idx) => {
                      const previousDate =
                        idx > 0 && check && check.text
                          ? new Date(
                              check.text?.split("|")[idx - 1].split(";")[2],
                            )
                          : undefined;
                      const currentDate = new Date(text.split(";")[2]);
                      const deltaTimeInMinutes = previousDate
                        ? differenceInMinutes(currentDate, previousDate)
                        : undefined;

                      return (
                        <div key={text} className="flex justify-between py-1.5">
                          <div className="flex items-center">
                            <div>
                              {text.split(";")[0] === "approved" ? (
                                <div className="bg-green-600 mr-1.5 h-5 w-5 flex items-center justify-center rounded-full">
                                  <BadgeCheck className="w-4 h-4 text-white" />
                                </div>
                              ) : (
                                <div className="bg-red-600 mr-1.5 h-5 w-5 flex items-center justify-center rounded-full">
                                  <BadgeAlert className="w-4 h-4 text-white" />
                                </div>
                              )}
                            </div>
                            <p className="text-sm truncate">
                              #{idx + 1} {text.split(";")[1]}
                            </p>
                          </div>

                          <div className="flex items-center">
                            <div className="flex items-center font-mono">
                              <p className="text-xs font-medium uppercase mr-2 text-black/30 whitespace-nowrap">
                                {format(currentDate, "dd MMM yy")}
                              </p>
                              <p className="text-xs font-medium whitespace-nowrap mr-2">
                                {format(currentDate, "HH:mm:ss")}
                              </p>
                              <p className="text-xs font-medium whitespace-nowrap">
                                +
                                {deltaTimeInMinutes === undefined
                                  ? 0
                                  : deltaTimeInMinutes}{" "}
                                min
                              </p>
                            </div>
                            {/* <div className="ml-2">
                              <X className="cursor-pointer hover:text-black w-4 h-4 text-muted-foreground" />
                            </div> */}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center">
                    <Button
                      onClick={() =>
                        handlePrint(
                          true,
                          check,
                          checklistId,
                          groupUid,
                          onFinish,
                        )
                      }
                      size={"sm"}
                      variant={"outline"}
                      className="ml-auto w-full mt-2 rounded-l-full border-2"
                    >
                      Goedkeuren
                    </Button>
                    <Button
                      onClick={() =>
                        handlePrint(
                          false,
                          check,
                          checklistId,
                          groupUid,
                          onFinish,
                        )
                      }
                      size={"sm"}
                      variant={"outline"}
                      className="ml-auto w-full mt-2 rounded-r-full border-r-2 border-y-2 border-l-0"
                    >
                      Afkeuren
                    </Button>
                  </div>
                </div>
              </AnswerBadge>
            </AnswerContainer>
          )}

          {!signed &&
            !check.text &&
            check.state !== CheckState.InApplicable && (
              <div className="w-full">
                <ActionContainer>
                  <PrimaryActionButton
                    onClick={() =>
                      handlePrint(true, check, checklistId, groupUid, onFinish)
                    }
                    disabled={check.loading}
                  >
                    {t("inspectionPage.checkPopup.okNokCheck.acceptButton")}
                  </PrimaryActionButton>
                </ActionContainer>

                {check.canBeInApplicable && (
                  <div className="px-4 pb-4">
                    <div className="border-t-2 border-dashed pt-4">
                      <SecondaryActionButton
                        onClick={handleCheckIsInApplicable}
                        disabled={check.loading}
                      >
                        {t("inspectionPage.checkPopup.inApplicableCheckButton")}
                      </SecondaryActionButton>
                    </div>
                  </div>
                )}
              </div>
            )}
        </CheckStateCard>
      </CheckPopupBase>
    );
  },
);
